<template>
  <div
    v-if="product"
    class="product-content-details"
  >
    <Heading
      v-if="headline"
      tag="h3"
      size="md"
      class="product-content-details__heading"
      :text="headline"
      spaces-bottom
    />
    <template v-for="(childProduct, key) in getProducts">
      <div
        v-if="shouldRenderEntry(childProduct)"
        :key="key"
        ref="sections"
        class="product-content-details__section"
      >
        <Heading
          v-if="product.bundle && childProduct?.family?.name"
          :tag="headline ? 'h4' : 'h3'"
          size="sm"
          class="product-content-details__product-heading"
          spaces-bottom
          :text="bundledFamilyNameWithCount(childProduct.family.name)"
        />
        <slot
          :product="childProduct"
          :offer="offer"
        />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { countBy, uniqBy } from 'lodash-es';
import {
  getBundleProduct,
  getStandardProduct,
  type Offer,
  type OfferBundleProduct,
  type OfferProduct,
  type OfferStandardProduct,
} from '~/lib/goliath/offer-by-slugs';
import { Heading } from '@i22-td-smarthome/component-library';

type BundleProduct = NonNullable<OfferBundleProduct['bundledProducts']>[number];

export default defineComponent({
  name: 'ProductContentDetails',
  components: { Heading },
  props: {
    offer: {
      type: Object as PropType<Offer>,
      required: true,
    },
    renderItemIf: {
      type: Function,
      default: undefined,
    },
    headline: {
      type: String,
      default: null,
    },
  },
  computed: {
    product(): OfferProduct | undefined {
      return this.offer?.product || undefined;
    },
    bundledProducts(): BundleProduct[] {
      if (!this.product) return [];
      const bundleProduct = getBundleProduct(this.product);
      if (bundleProduct) {
        return bundleProduct.bundledProducts || [];
      }
      return [];
    },
    getProducts(): (BundleProduct | OfferStandardProduct)[] {
      if (!this.product) return [];
      return this.groupedBundledProducts.length > 0
        ? this.groupedBundledProducts
        : [getStandardProduct(this.product)];
    },
    groupedBundledProducts(): BundleProduct[] {
      return uniqBy(this.bundledProducts, (p: BundleProduct) => p?.family?.name);
    },
    bundledProductFamilyCounts() {
      return countBy(this.bundledProducts, (p) => p.family?.name);
    },
  },
  methods: {
    bundledFamilyNameWithCount(familyName: string) {
      const count = this.bundledProductFamilyCounts[familyName] || 0;
      if (count <= 1) return familyName;
      return `${count} × ${familyName}`;
    },
    shouldRenderEntry(product) {
      if (this.renderItemIf) {
        return !!this.renderItemIf(product);
      }
      return true;
    },
  },
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.product-content-details {
  &__heading {
    @include mq(false, $mq-sm - 1) {
      display: none;
    }
  }

  &__product-heading {
    @include mq(false, $mq-md - 1) {
      font-size: 24px;
    }
  }

  &__section {
    margin-bottom: $grid-base;
    border-bottom: 1px solid color(grey, 30000);
    padding-bottom: $grid-base;
  }
}
</style>
